import Fuse from 'fuse.js';
import React, { useState, useEffect, useMemo } from 'react';
import { useAnalytics } from 'telia-front-react';
import { JSONController, URLData } from '@telia-common-components/shared';
import { TvComparisonContext } from './TvComparisonContext';
const defaultTvComparisonPackagesResource = {
    packagePrices: [],
    themePackages: [],
    channels: [],
    categoriesList: {},
    additionalServices: [],
    packageNames: [],
    viewerSlots: {},
    packageSizes: [],
    apps: [],
};
const TvComparisonProvider = ({ lang, tier, translate, children }) => {
    const { push: pushAnalytics } = useAnalytics();
    const [packagesResponse, setPackagesResponse] = useState(defaultTvComparisonPackagesResource);
    const [filterState, setFilterState] = useState({
        channelSuggestionItems: [],
        selectedItem: null,
        fuzzySearch: null,
        selectedPackages: [],
        selectedThemes: [],
        selectedApps: [],
        selectedServices: [],
        isAllChannelsShown: false,
        isAllThemesShown: false,
        isAllServicesShown: false,
        isAllAppsShown: false,
    });
    const packagesJsonController = useMemo(() => new JSONController(), []);
    const urlData = useMemo(() => new URLData({ app: 'public', tier, lang }), [tier, lang]);
    const fuzzySearchOptions = {
        shouldSort: true,
        keys: ['label'],
        threshold: 0.2,
    };
    useEffect(() => {
        const getPackages = async () => {
            try {
                const response = await packagesJsonController.fetch(urlData.getTvComparisonDataUrl(lang));
                setPackagesResponse(response);
                setFuzzyState(response);
            }
            catch (e) {
                setPackagesResponse(defaultTvComparisonPackagesResource);
                console.error(e);
            }
        };
        getPackages();
    }, [lang, packagesJsonController, urlData]);
    const setFuzzyState = (resp) => {
        setFilterState((prev) => ({
            ...prev,
            fuzzySearch: new Fuse(suggestionItems(resp), fuzzySearchOptions),
        }));
    };
    const suggestionItems = (resp) => {
        return [
            ...resp.channels.map((c) => ({
                id: `${c.id}`,
                label: c.title,
                url: '',
            })),
            ...resp.themePackages.map((tp) => ({
                id: `${tp.productSpecId}`,
                label: `${translate(`tvcomparison.themepackages.${tp.productOfferingCode}.label`)}`,
                url: '',
            })),
            ...resp.additionalServices.map((service) => ({
                id: `${service.productSpecId}`,
                label: `${translate(`tvcomparison.services.${service.productOfferingCode}.label`)}`,
                url: '',
            })),
            ...resp.apps.map((app) => ({
                id: `tvcomparison-search-suggestion-app-${app.productOfferingCode}`,
                label: `${translate(`tvcomparison.apps.${app.productOfferingCode}.label`)}`,
                url: '',
            })),
        ];
    };
    const selectSuggestionItem = (item) => {
        setFilterState((prev) => ({ ...prev, selectedItem: item }));
        if (item === null || item === void 0 ? void 0 : item.label) {
            pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(item.label);
        }
    };
    const setSelectedThemes = (themes) => {
        setFilterState((prev) => ({ ...prev, selectedThemes: themes }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Selected themes ${themes.concat(', ')}`);
    };
    const setSelectedPackages = (packages) => {
        setFilterState((prev) => ({ ...prev, selectedPackages: packages }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Selected packages ${packages.concat(', ')}`);
    };
    const setSelectedApps = (apps) => {
        setFilterState((prev) => ({ ...prev, selectedApps: apps }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Selected apps ${apps.concat(', ')}`);
    };
    const setSelectedServices = (services) => {
        setFilterState((prev) => ({ ...prev, selectedServices: services }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Selected services ${services.concat(', ')}`);
    };
    const toggleAllThemesShown = (event) => {
        event.preventDefault();
        const isAllThemesShown = filterState.isAllThemesShown;
        setFilterState((prev) => ({
            ...prev,
            isAllThemesShown: !prev.isAllThemesShown,
        }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Toggle show ${isAllThemesShown ? 'less' : 'all'} themes`);
    };
    const toggleAllChannelsShown = (event) => {
        event.preventDefault();
        const isAllChannelsShown = filterState.isAllChannelsShown;
        setFilterState((prev) => ({
            ...prev,
            isAllChannelsShown: !prev.isAllChannelsShown,
        }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Toggle show ${isAllChannelsShown ? 'less' : 'all'} channels`);
    };
    const toggleAllServicesShown = (event) => {
        event.preventDefault();
        const isAllServicesShown = filterState.isAllServicesShown;
        setFilterState((prev) => ({
            ...prev,
            isAllServicesShown: !prev.isAllServicesShown,
        }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Toggle show ${isAllServicesShown ? 'less' : 'all'} services`);
    };
    const toggleAllAppsShown = (event) => {
        event.preventDefault();
        const isAllAppsShown = filterState.isAllAppsShown;
        setFilterState((prev) => ({
            ...prev,
            isAllAppsShown: !prev.isAllAppsShown,
        }));
        pushAnalytics === null || pushAnalytics === void 0 ? void 0 : pushAnalytics(`Toggle show ${isAllAppsShown ? 'less' : 'all'} apps`);
    };
    return (React.createElement(TvComparisonContext.Provider, { value: {
            packagesResponse,
            filterState,
            filterActions: {
                selectSuggestionItem,
                setSelectedPackages,
                setSelectedThemes,
                setSelectedApps,
                setSelectedServices,
                toggleAllThemesShown,
                toggleAllChannelsShown,
                toggleAllServicesShown,
                toggleAllAppsShown,
            },
            urlData,
        } }, children));
};
export default TvComparisonProvider;
